import { useState } from "react";
import { Link } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";
import PageHero from "@/components/feature/PageHero";
import SiteLayout from "@/components/feature/SiteLayout";
import RankList from "@/components/base/RankList";
import RankTabs from "@/pages/ranking/components/RankTabs";
import RankPodium from "@/pages/ranking/components/RankPodium";
import { rankRising, rankTotal, rankWeekly } from "@/mocks/rankings";
import { categories } from "@/mocks/categories";

const tabs = [
  { key: "total", label: "热门总榜", desc: "实时讨论量排序" },
  { key: "weekly", label: "本周盘点榜", desc: "每周一上午结算" },
  { key: "rising", label: "新晋话题榜", desc: "近三天首次进前二十" },
];

const boards = {
  total: rankTotal,
  weekly: rankWeekly,
  rising: rankRising,
};

export default function Ranking() {
  const [active, setActive] = useState("total");
  const current = boards[active as keyof typeof boards];
  const maxCategoryCount = Math.max(...categories.map((c) => c.count));

  const listSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "爆料吃瓜热门盘点榜单",
    description:
      "按讨论量排序的爆料吃瓜热门盘点榜单，包含热门总榜、本周盘点榜与新晋话题榜。",
    inLanguage: "zh-CN",
    itemListElement: current.map((item) => ({
      "@type": "ListItem",
      position: item.rank,
      name: item.title,
      url: `https://dearyuan.cn/article/${item.slug}`,
    })),
  };

  return (
    <>
      <PageMeta
        title="热门盘点榜单｜爆料吃瓜话题热度实时排行"
        description="爆料吃瓜热门盘点榜单按讨论量实时排序，提供热门总榜、本周盘点榜与新晋话题榜，帮你在最短时间内抓住当天最值得关注的事件汇总。"
        keywords="爆料吃瓜热门盘点,爆料吃瓜榜单,话题热度排行,事件汇总,本周盘点"
        canonical="/ranking"
      />
      <JsonLd data={listSchema} />

      <SiteLayout>
        <PageHero
          eyebrow="热门盘点"
          title="榜单说话：哪些话题真的在发酵"
          desc="总榜每两小时刷新，周榜每周一上午 10:00 结算，新晋榜收录近三天首次进入前二十的话题。"
          crumbs={[{ label: "热门盘点" }]}
        >
          <div className="flex gap-6 text-secondary-50">
            <div>
              <p className="font-heading text-3xl font-black">2 小时</p>
              <p className="mt-1 text-xs text-secondary-100/70">榜单刷新间隔</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-black">3 个</p>
              <p className="mt-1 text-xs text-secondary-100/70">榜单类型</p>
            </div>
          </div>
        </PageHero>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <RankTabs
            tabs={tabs}
            value={active}
            onChange={(key) => setActive(key)}
          />

          <div className="mt-8">
            <RankPodium items={current} />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            <div className="rounded-card border border-background-200/80 bg-background-50 p-3 md:p-4">
              <h2 className="px-3 pt-2 font-heading text-base font-bold text-foreground-950">
                {tabs.find((t) => t.key === active)?.label} · 完整排名
              </h2>
              <div className="mt-3">
                <RankList items={current} />
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-card border border-background-200/80 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-pie-chart-2-line text-secondary-600"></i>
                  </span>
                  分类内容量分布
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {categories.map((cat) => (
                    <Link key={cat.slug} to={`/daily?category=${cat.slug}`} className="group block">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-700 transition-colors duration-200 group-hover:text-primary-700">
                          {cat.name}
                        </span>
                        <span className="text-foreground-500">{cat.count} 条</span>
                      </div>
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200/80">
                        <div
                          className="h-full rounded-full bg-secondary-500 transition-colors duration-200 group-hover:bg-primary-500"
                          style={{ width: `${Math.round((cat.count / maxCategoryCount) * 100)}%` }}
                        ></div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-card border border-background-200/80 bg-accent-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">榜单怎么算</h2>
                <ul className="mt-3 flex flex-col gap-2.5 text-xs leading-relaxed text-foreground-600">
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                    讨论量来自站内浏览、收藏与转发的加权合计。
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                    同一事件的不同话题会合并计算，避免重复占位。
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                    已被澄清或证伪的信息会被移出榜单，并在原页面标注。
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </SiteLayout>
    </>
  );
}